import { Component, Inject, OnInit, ViewEncapsulation } from '@angular/core';
import { FormControl } from '@angular/forms';
import { MAT_DIALOG_DATA } from '@angular/material/dialog';
import {
  MaintenanceAndTasksLogsMapped,
  MaintenanceLog,
  MaintenanceLogSelectOption,
  Task
} from '@core/interfaces';
import camelCaseToSnakeCase from '@core/utils/functions/camel-case-to-snake-case';
import { EnumPipe } from '@fuse/pipes/status/status.pipe';
import { TranslateService } from '@ngx-translate/core';
import { concat, orderBy, sortBy } from 'lodash';
import moment from 'moment-timezone';

import { MaintenanceDialogData } from '../../dialog-maintenances.component';
import { CreateMaintenanceLogsService } from './services/create-maintenance-logs.service';
import { CreateTaskLogsService } from './services/create-task-logs.service';
import { UpdatedByLogService } from './services/updated-by-log.service';

@Component({
  selector: 'tab-logs',
  templateUrl: './tab-logs.component.html',
  styleUrls: ['./tab-logs.component.scss'],
  encapsulation: ViewEncapsulation.None
})
export class TabLogsComponent implements OnInit {
  public readonly todayDate = moment().format('DD/MM/YYYY');
  public readonly yesterdayDate = moment().subtract(1, 'days').format('DD/MM/YYYY');

  private maintenanceLogs: MaintenanceLog[];
  private maintenanceTasks: Task[];

  public logsMapped: MaintenanceAndTasksLogsMapped[] = [];
  public logsMappedFiltered: MaintenanceAndTasksLogsMapped[] = [];

  public selectOptions: MaintenanceLogSelectOption[] = [
    {
      title: this.translateService.instant('MAINTENANCES.TABS.LOGS.FILTER.GENERAL'),
      value: 'general'
    },
    {
      title: this.translateService.instant('MAINTENANCES.TABS.LOGS.FILTER.MAINTENANCE'),
      value: 'maintenance'
    }
  ];

  public selectValue = new FormControl(this.selectOptions[0]);

  constructor(
    @Inject(MAT_DIALOG_DATA) data: MaintenanceDialogData,
    private taskStatusPipe: EnumPipe,
    private translateService: TranslateService,
    private updatedByLogService: UpdatedByLogService,
    private createMaintenanceLogsService: CreateMaintenanceLogsService,
    private createTasksLogsService: CreateTaskLogsService,
  ) {
    this.maintenanceLogs = data.maintenance.logs || [];
    this.maintenanceTasks = data.maintenance.tasks || [];
  }

  ngOnInit(): void {
    this.defineSelectOptions();
    this.createLogsMapped();
    this.filterLogs('general');

    this.selectValue.valueChanges
      .subscribe((optionSelected) => this.filterLogs(optionSelected.value));
  }

  private defineSelectOptions(): void {
    this.maintenanceTasks.forEach((task, taskIndex) => {
      const taskStatusTranslated = this.taskStatusPipe.transform(String(task.status), 'task_status');
      const taskStatusClass = camelCaseToSnakeCase(String(task.status));

      let title = null;
      const { length, [length - 1]: last } = task.logs;
      if (last && last.snapshot && last.snapshot.archived) {
        title = this.translateService.instant('MAINTENANCES.TABS.LOGS.FILTER.TASK_ARCHIVED', {
          taskIndex: taskIndex + 1,
        });
      } else {
        title = this.translateService.instant('MAINTENANCES.TABS.LOGS.FILTER.TASK', {
          taskIndex: taskIndex + 1,
          class: taskStatusClass,
          status: taskStatusTranslated
        });
      }

      this.selectOptions.push({
        title,
        value: `task_${task.id}`
      });
    });
  }

  private filterLogs(filterLogsBy: string): void {
    if (filterLogsBy === 'general') {
      this.logsMappedFiltered = this.logsMapped;
      return;
    }

    const logsFiltered = this.logsMapped.filter((log) => log.typeLog === filterLogsBy);
    this.logsMappedFiltered = sortBy(logsFiltered);
  }

  private sortByReverseCreatedAt(logs: MaintenanceAndTasksLogsMapped[]): MaintenanceAndTasksLogsMapped[] {
    return orderBy(logs, ['createdAt'], ['desc']);
  }

  private createLogsMapped(): void {
    const maintenanceLogs = this.createMaintenanceLogsService.listMaintenancesLogsMapped(this.maintenanceLogs);
    const tasksLogsMapped = this.createTasksLogsService.listTaskLogsMapped(this.maintenanceTasks);
    const allLogsConcatenated = concat(maintenanceLogs, tasksLogsMapped);
    const allLogsWithChangedBy = this.updatedByLogService.customUpdatedByInLogs(allLogsConcatenated);

    this.logsMapped = this.sortByReverseCreatedAt(allLogsWithChangedBy);
  }
}
